import { AxiosError } from "axios";

export type PageMeta = {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
};

export interface GeneralResponse<T> {
  code: number;
  data: T;
  errors: any;
  message: string;
  pageMeta: PageMeta;
  succeeded: boolean;
}

export type DataErrorResponse<T> = {
  data?: GeneralResponse<T>;
  error?: AxiosError<any>;
};

export type DataErrorResponseUnwrapped<T> = {
  data?: T;
  error?: AxiosError<any>;
};

export type MortgageDocumentDetail = {
  id: number;
  mortgageApplicationId: number;
  documentType: number;
  status: number;
  uploadedAt: string;
  fileName: string;
  fileBlobUrl: string;
};

export type RepaymentScheduleItem = {
  id: number;
  mortgageApplicationId: number;
  paymentNumber: number;
  openingBalance: number;
  paymentAmount: number;
  principalPaid: number;
  interestPaid: number;
  closingBalance: number;
};

export type ExternalMortgagePropertyDetail = {
  id: number;
  mortgageApplicationId: number;
  propertyName: string;
  address: string;
  propertyType: string;
  propertyValue: number;
  sellerName: string;
  sellerPhone: string;
  sellerEmail: string;
  titleDocumentType: number;
};

export type IncomeProfileDetail = {
  id: number;
  mortgageApplicationId: number;
  basicSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  otherAllowances: number;
  annualBonus: number;
  monthlyBonusEquivalent: number;
  otherIncome: number;
  exchangeRateUsed: number;
  totalOriginalMonthlyIncome: number;
  totalPrimaryIncomeNGN: number;
  totalConvertedMonthlyIncome: number;
  annualIncome: number;
  averageMonthlyBusinessProfit: number;
  auditedAnnualRevenue: number;
  coBorrowerMonthlyIncome: number;
  coBorrowerCurrency: string;
  coBorrowerIncomeNGN: number;
  coBorrowerExchangeRate: number;
  numberOfDependents: number;
  qualifications: string;
  workExperience: string;
  currentEmployerName: string;
  employerBusiness: string;
  employmentStatus: string;
  typeOfEmployment: string;
  employerAddress: string;
  jobDesignation: string;
  currency: string;
  fxRateRetrievedAt: string;
};

export type PrequalifyResponseData = {
  id: number;
  userId: string;
  source: number;
  location: number;
  employmentType: number;
  loanAmount: number;
  downPayment: number;
  upfrontFees: number;
  effectiveLoanAmount: number;
  interestRate: number;
  tenorInMonths: number;
  repaymentFrequency: number;
  useRSA: boolean;
  monthlyIncomeNGN: number;
  monthlyRepayment: number;
  totalRepaymentAmount: number;
  totalInterestPaid: number;
  totalUpfrontCost: number;
  debtToIncomeRatio: number;
  dtiBenchmark: number;
  isEligible: boolean;
  availableMonthlyDsrHeadroom: number;
  maxEligibleLoanAmount: number;
  titleSearchPaymentMade: boolean;
  bvnVerified: boolean;
  hasRunningLoan: boolean;
  bureauMonthlyRepayment: number;
  bureauOutstandingDebt: number;
  existingMonthlyObligations: number;
  totalOutstandingDebt: number;
  creditCheckCompleted: boolean;
  creditCheckConsentGiven: boolean;
  coBorrowerUseRSA: boolean;
  accessBankAccountVerified: boolean;
  mortgageApplicationStatus: number;
  createdAt: string;
  documents: MortgageDocumentDetail[];
  repaymentSchedule: RepaymentScheduleItem[];
  bvn: string;
  propertyId: number;
  externalMortgageProperty: ExternalMortgagePropertyDetail | null;
  mortgageFunderId: number;
  dateOfBirth: string;
  rsaNumber: string;
  pensionAdmin: string;
  salaryStructure: string;
  incomeCurrency: string;
  eligibilityMessage: string;
  titleSearchPaymentReference: string;
  titleSearchReceiptUrl: string;
  bvnVerifiedAt: string;
  creditCheckAt: string;
  coBorrowerRSANumber: string;
  coBorrowerPensionAdmin: string;
  pfaBalance: number;
  pfaContributionAmount: number;
  coBorrowerPfaBalance: number;
  coBorrowerPfaContributionAmount: number;
  hasAccessDiasporaNairaAccount: boolean;
  bvnFirstName: string;
  bvnMiddleName: string;
  bvnLastName: string;
  bvnGender: string;
  bvnEmail: string;
  bvnPhoneNumber: string;
  bvnResidentialAddress: string;
  bvnStateOfOrigin: string;
  bvnLga: string;
  bvnNationality: string;
  bvnMaidenName: string;
  bvnPlaceOfBirth: string;
  bvnReligion: string;
  bvnPassportPhotoUrl: string;
  bankStatementBlobUrl: string;
  bankStatementSource: string;
  accessBankAccountNumber: string;
  accessBankAccountEmail: string;
  accessBankAccountVerifiedAt: string;
  applicationReferenceNumber: string;
  conditionalOfferLetterSentAt: string;
  preQualifiedAt: string;
  documentsUploadedAt: string;
  submittedAt: string;
  forwardedToBankAt: string;
  incomeProfile: IncomeProfileDetail | null;
};

export type VerifyBvnSendOtpRequest = {
  bvn: string;
  consentGiven: boolean;
};

export type VerifyBvnVerifyOtpRequest = {
  bvn: string | null;
  otp: string;
};

export type GetExistingLoansResData = {
  recordFound: boolean;
  bureauResponseMessage: string;
  surname: string;
  firstName: string;
  otherNames: string;
  birthDate: string;
  hasActiveLoans: boolean;
  numberOfActiveFacilities: number;
  existingMonthlyRepayment: number;
  totalOutstandingDebt: number;
  creditCheckStatus: "worthy" | string;
};

export type SubmitExistingLoansResData = {
  mortgageApplicationId: number;
  hasRunningLoan: boolean;
  bureauMonthlyRepayment: number;
  bureauOutstandingDebt: number;
  additionalMonthlyObligations: number;
  additionalOutstandingDebt: number;
  existingMonthlyObligations: number;
  totalOutstandingDebt: number;
  message: string;
};

export type VerifyBvnVerifyOtpResponseData = {
  mortgageApplicationId: number;
  bvn: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
  email: string;
  residentialAddress: string;
  passportPhotoUrl: string;
  stateOfOrigin: string;
  lga: string;
  nationality: string;
  placeOfBirth: string;
  religion: string;
  maidenName: string;
};

export type InitiateCreditCheckRequest = {
  mortgageApplicationId: number;
  location: number;
};

export type ValidateAccountRequest = {
  accountNumber: string;
  mortgageApplicationId: number;
};

export type ValidateAccountVerifyOtpRequest = {
  mortgageApplicationId: number;
  otp: string;
};

export type InitiateCreditCheckResData = {
  recordFound: boolean;
  hasActiveLoans: boolean;
  numberOfActiveFacilities: number;
  existingMonthlyRepayment: number;
  totalOutstandingDebt: number;
  mortgageApplicationId: number;
  message: string;
  creditCheckStatus: string;
};

export type VerifyBvnSendOtpResData = {
  maskedContact: string;
  message: string;
};

export type GenerateAccountStatementResData = {
  mortgageApplicationId: number;
  success: boolean;
  uploadedAt: string;
  bankStatementBlobUrl: string;
  bankStatementSource: string;
  message: string;
};

export type InitiateCreditCheckResponse = DataErrorResponse<
  InitiateCreditCheckResData | InitiateCreditCheckResData[]
>;
export type VerifyBvnSendOtpResponse =
  DataErrorResponse<VerifyBvnSendOtpResData>;
export type VerifyBvnVerifyOtpResponse =
  DataErrorResponse<VerifyBvnVerifyOtpResponseData>;
export type SubmitExistingLoansResponse =
  DataErrorResponse<SubmitExistingLoansResData>;
export type GetExistingLoansResponse =
  DataErrorResponseUnwrapped<GetExistingLoansResData>;
export type GenerateAccountStatementResponse =
  DataErrorResponse<GenerateAccountStatementResData>;

export type UploadDocumentResult = {
  documentType: number;
  success: boolean;
  fileName: string;
  error: string;
  document: MortgageDocumentDetail;
};

export type AttachPropertyResponseData = PrequalifyResponseData;
export type MortDocumentUploadResponseData = {
  results: UploadDocumentResult[];
  successCount: number;
  failureCount: number;
};
export type SubmitMortgageResponseData = string;

export type PrequalifyResponse = DataErrorResponse<PrequalifyResponseData>;
export type ConvertCurrencyResponse =
  DataErrorResponse<ConvertCurrencyResponseData>;
export type ValidateAccountResponse =
  DataErrorResponse<ValidateAccountResponseData>;
export type AttachPropertyResponse =
  DataErrorResponse<AttachPropertyResponseData>;
export type MortDocumentUploadResponse =
  DataErrorResponse<MortDocumentUploadResponseData>;
export type SubmitMortgageResponse =
  DataErrorResponse<SubmitMortgageResponseData>;
export type ValidateAccountVerifyOtpResponse =
  DataErrorResponse<SubmitMortgageResponseData>;

export type PrequalifyRequest = {
  mortgageApplicationId?: number;
  source: number;
  location: number;
  applicantType: number;
  loanAmount: number;
  downPayment: number;
  upfrontFees: number;
  interestRate: number;
  tenorInMonths: number;
  currency: string;
  repaymentFrequency: number;
  monthlyIncome: number;
  coBorrowerMonthlyIncome: number;
  coBorrowerCurrency: string;
  dateOfBirth: string;
  useRSA: boolean;
  rsaNumber: string;
  pensionAdmin: string;
  pfaBalance?: number;
  coBorrowerUseRSA?: boolean;
  coBorrowerRSANumber?: string;
  coBorrowerPensionAdmin?: string;
  coBorrowerPfaBalance?: number;
  mortgageFunderId?: number;
  hasAccessDiasporaNairaAccount?: boolean;
  propertyId?: number;
  bvn: string;
  hasCoBorrower?: boolean;
  hasRSA?: boolean;
  coBorrowerHasRSA?: boolean;
};

export type ConvertCurrencyRequest = {
  amount: number;
  fromCurrency: string;
};

export type ConvertCurrencyResponseData = {
  originalAmount: number;
  fromCurrency: string;
  convertedAmountNGN: number;
  exchangeRate: number;
  toCurrency: "NGN";
};

export type ValidateAccountResponseData = {
  maskedContact: string;
  bvn: string;
  message: string;
};

export type ValidateAccountVerifyOtpResData = {
  isValid: boolean;
  accountNumber: string;
  accountName: string;
  currencyCode: string;
  bvn: string;
  emailAddress: string;
  message: string;
};

export type AttachPropertyRequest = {
  mortgageApplicationId: number;
  externalProperty: {
    propertyName: string;
    address: string;
    propertyType: string;
    propertyValue: number;
    sellerName: string;
    sellerPhone: string;
    sellerEmail: string;
  };
};

export type DocumentFormat = {
  uri: string;
  name: string;
  filename: string;
  type: string;
};

export type UploadDocExtMortageReq = {
  MortgageApplicationId: number;
  DocumentTypes: number[];
  Files: File[];
};

export type UploadDocMortSalaryBusinessReq = {
  hasAccessAccount: boolean;
  accountNumber: string;
  accountName: string;
  bankName: string;
  statementOfAccount?: File;
  meansOfIdentification: File;
  letterOfEmployment?: File;
  paySlip?: File;
  evidenceOfPayment?: File;
  businessRegistration?: File;
  proofOfResidence?: File;
  taxEvidence?: File;
  w2?: File;
  nationalInsurance?: File;
};

export type ExistingLoan = {
  outstandingLoanAmount: number;
  monthlyRepayment: number;
  loanTenor: number;
  remainingTenure: number;
  lendingInstitution: string;
};

export type SubmitExistingLoansRequest = {
  hasAdditionalLoans: boolean;
  mortgageApplicationId: number;
  existingLoans: ExistingLoan[];
};

export type GenerateAccountStatementRequest = {
  fromDate: string;
  toDate: string;
};

export const DocumentTypes = [
  { document: "Payslip", value: 0, finder: "paySlip" },
  { document: "EmploymentLetter", value: 1, finder: "letterOfEmployment" },
  { document: "BankStatement", value: 2, finder: "statementOfAccount" },
  { document: "BusinessBankStatement", value: 3, finder: "" },
  { document: "MeansOfId", value: 4, finder: "meansOfIdentification" },
  {
    document: "BusinessRegistration",
    value: 5,
    finder: "businessRegistration",
  },
  { document: "ProofOfResidence", value: 6, finder: "proofOfResidence" },
  { document: "DiasporaIncomeVerification", value: 7, finder: "" },
  {
    document: "SellerOfferAcceptanceLetter",
    value: 8,
    finder: "aceptanceLetter",
  },
  { document: "TitleDocument", value: 9, finder: "titledDocument" },
  { document: "PropertyPhoto", value: 10, finder: "propertyPhoto" },
  { document: "W2", value: 11, finder: "w2" },
  { document: "NationalInsurance", value: 12, finder: "nationalInsurance" },
  { document: "TaxEvidence", value: 13, finder: "taxEvidence" },
  { document: "TitleSearchReceipt", value: 14, finder: "" },
  { document: "AdditionalPayment", value: 15, finder: "evidenceOfPayment" },
  { document: "Other", value: 16, finder: "" },
];

export type GetRentFinanceResponse = DataErrorResponse<{ url?: string }>;
